import type { ZodType, z } from "zod";
import { create } from "zustand";
import { createJSONStorage, type PersistOptions, persist } from "zustand/middleware";
import { raisePersistedSearchParamsReset } from "./persistedSearchParams/createPersistedSearchParams.registry";

/**
 * localStorage namespace for feature-owned preference slices.
 * Also used to sweep slices whose module was never imported (see `resetAllLocalPrefs`).
 */
const PREFS_KEY_PREFIX = "definity:prefs:";

/** id → reset, filled as feature modules are imported. */
const sliceResetters = new Map<string, () => void>();

export type LocalPrefs<T extends object> = {
  /** Zustand bound hook: `prefs.usePrefs((s) => s.someField)`. */
  usePrefs: <S>(selector: (state: T) => S) => S;
  getPrefs: () => T;
  /** Shallow merge — other fields of the slice are preserved. */
  patchPrefs: (patch: Partial<T>) => void;
  resetPrefs: () => void;
};

const unwrapPersistPayload = (parsed: unknown): unknown =>
  parsed !== null && typeof parsed === "object" && "state" in parsed
    ? (parsed as { state: unknown }).state
    : parsed;

/**
 * Creates a persisted preference slice owned by one feature.
 *
 * The feature declares its own key, defaults, schema and version, so shared storage
 * code never accumulates feature-specific types or setters, and each slice migrates
 * independently of `global-context` / search-storage.
 *
 * Call it once at module scope of the feature (never inside a component) and export the
 * pieces the feature needs. Initial state is read from localStorage in the same tick as
 * `create()`, so the first render already has stored values — persist middleware only
 * keeps later writes in sync (its own rehydrate is async).
 *
 * A slice holds plain serializable values only — actions live on the returned object,
 * not in the store, so `resetPrefs` can replace the whole state.
 *
 * ```ts
 * const prefs = createLocalPrefs({
 *   id: "myFeature",
 *   version: 1,
 *   defaults: { sortBy: null },
 *   schema: z.object({ sortBy: z.string().nullable().default(null) }),
 * });
 * ```
 */
export const createLocalPrefs = <S extends ZodType<object>>({
  id,
  version = 1,
  defaults,
  schema,
  migrate,
}: {
  /** Unique per feature; becomes `definity:prefs:<id>`. */
  id: string;
  version?: number;
  defaults: z.infer<S>;
  /** Validated on rehydrate — a payload that fails falls back to `defaults`. */
  schema: S;
  migrate?: PersistOptions<z.infer<S>>["migrate"];
}): LocalPrefs<z.infer<S>> => {
  type T = z.infer<S>;
  const name = PREFS_KEY_PREFIX + id;

  const applyStored = (persisted: unknown, fallback: T): T => {
    const parsed = schema.safeParse(persisted);
    // Whole-payload failure → defaults. Do not use per-field `.catch()` in slice
    // schemas if you want this (a catching field would make safeParse succeed).
    return parsed.success ? ({ ...fallback, ...parsed.data } as T) : fallback;
  };

  const readInitial = (): T => {
    try {
      const raw = localStorage.getItem(name);
      if (!raw) return { ...defaults };
      return applyStored(unwrapPersistPayload(JSON.parse(raw)), { ...defaults });
    } catch {
      return { ...defaults };
    }
  };

  const store = create<T>()(
    persist((): T => readInitial(), {
      name,
      version,
      storage: createJSONStorage(() => localStorage),
      migrate,
      merge: (persisted, current) => applyStored(persisted, current),
    })
  );

  const resetPrefs = () => store.setState({ ...defaults }, true);

  sliceResetters.set(id, resetPrefs);

  return {
    usePrefs: store,
    getPrefs: store.getState,
    // Functional merge so `null` is a real write (`{ key: null }`), not a no-op omit.
    // Default PIT / cleared controls persist as `null` so they are not resurrected.
    patchPrefs: (patch) => {
      store.setState((current) => ({ ...current, ...patch }));
    },
    resetPrefs,
  };
};

/**
 * Clears every feature preference slice — called by `useGlobalLocalContext.resetAll`.
 *
 * Registered slices are reset through their store so mounted components re-render. The
 * registry only holds features whose module has been imported, so the loop afterwards
 * covers the rest: keys left behind by code-split routes the user never opened this
 * session, which would otherwise survive a "reset everything".
 */
export const resetAllLocalPrefs = (): void => {
  sliceResetters.forEach((resetPrefs) => resetPrefs());

  // Storage keys live on the exotic Storage object; iterate via length/key,
  // not Object.keys (that is the mock- and spec-correct enumerator).
  for (let i = localStorage.length - 1; i >= 0; i -= 1) {
    const key = localStorage.key(i);
    if (!key?.startsWith(PREFS_KEY_PREFIX)) continue;
    if (sliceResetters.has(key.slice(PREFS_KEY_PREFIX.length))) continue;
    localStorage.removeItem(key);
  }

  raisePersistedSearchParamsReset();
};

/** Test-only: drop registered resetters between cases. */
export const resetLocalPrefsRegistry = (): void => {
  sliceResetters.clear();
};
